import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { IContext } from './columns'
import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IChecklist } from '~/types/checklist'
import type { IUserPublic } from '~/types/user'

import columns from './columns'
import { checklistsKeys, useChecklistsListQuery } from './queries'

import ChecklistModal from '~/components/ChecklistModal'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'
import { useUserDetailsQueries } from '~/queries/users'
import { keys as versionsKeys } from '~/queries/versions'
import { transformByDeviceType } from '~/utils/application'
import { displayToast } from '~/utils/toast'

type UserProfile =
  | IUserPublic
  | {
      user_id: string
    }
  | undefined

type ChecklistWithIUser = Omit<IChecklist, 'created_by' | 'updated_by'> & {
  created_by: UserProfile
  updated_by: UserProfile
}

const Checklists = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const tableRef = useRef<HTMLDivElement>(null)

  const gridRef = useRef<AgGridReact>(null)

  const queryClient = useQueryClient()

  const checklistsManagementAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.TODOLIST.WRITE])

  const [isCreateToDoModalOpen, setIsCreateToDoModalOpen] = useState(false)

  const [checklist, setTodo] = useState<IChecklist>()

  /** Queries & Mutations */

  const { appDetails, appKey, deviceType } = useAppSummary()

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  const { data: checklists, isFetched: checklistsFetched } = useChecklistsListQuery(appSummary?.app_id)

  const createdByQueries =
    checklists?.data?.filter(({ created_by }) => created_by).map(({ created_by }) => created_by) || []

  const updatedByQueries =
    checklists?.data?.filter(({ updated_by }) => updated_by).map(({ updated_by }) => updated_by) || []

  const userQueries = useUserDetailsQueries([...createdByQueries, ...updatedByQueries])

  const userLoading = userQueries.some((e) => e.isLoading)

  const users: { user_id: string }[] | undefined = !userLoading
    ? userQueries.map(({ data }) => data || { user_id: '' })
    : undefined

  const rowData: ChecklistWithIUser[] | undefined =
    checklistsFetched && users
      ? checklists?.data?.map((checklist) => ({
          ...checklist,
          created_by: users?.find((user) => user.user_id === checklist.created_by),
          updated_by: users?.find((user) => user.user_id === checklist.updated_by),
        })) || []
      : undefined

  /** Effects */

  useDocumentTitle('To-do lists')

  /** Handlers */

  const handleEditTodo = (checklist: IChecklist) => {
    setTodo(checklist)
    setIsCreateToDoModalOpen(true)
  }

  const handleRedirect = (path: string) => navigate(path)

  const handleRowClicked = (evt: RowClickedEvent) => {
    if (gridRef.current?.api.getFocusedCell()?.column.getColId() !== 'actions') {
      navigate(`/checklists/${evt.data.id}`)
    }
  }

  const handleCreateTodoModal = (value: boolean) => () => {
    setIsCreateToDoModalOpen(value)
    setTodo(undefined)
  }

  const handleAddToDoSuccess = () => {
    setIsCreateToDoModalOpen(false)
    displayToast(`To-do successfully ${checklist ? 'updated' : 'created'}`, 'success')

    if (checklist?.application_version_id) {
      queryClient.invalidateQueries({ queryKey: versionsKeys.details(checklist.application_version_id) })
    }

    queryClient.invalidateQueries({ queryKey: checklistsKeys.list(appSummary?.app_id) })
    setTodo(undefined)
  }

  const handleAddToDoFailure = () => {
    setIsCreateToDoModalOpen(false)
    displayToast(`To-do could not be ${checklist ? 'updated' : 'created'}`, 'error')
    setTodo(undefined)
  }

  /** Render */

  const context: IContext = {
    application: `${appKey}:${deviceType}`,
    handleEditTodo,
    handleRedirect,
  }

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="checklists-view" withPadding>
        <ViewTitle className="layout-view-title">App to-do lists</ViewTitle>
        <ContentLayout loading={!appSummary}>
          <Section>
            <TableWrapper
              addButton
              addButtonDisabled={!checklistsManagementAuthorized}
              addButtonLabel="New to-do list"
              addButtonTooltip={!checklistsManagementAuthorized ? 'unauthorized' : undefined}
              className="checklists-table with-min-height"
              columnDefs={columns}
              context={context}
              entriesName="to-do lists"
              onAddButtonClick={handleCreateTodoModal(true)}
              onRowClicked={handleRowClicked}
              ref={gridRef}
              rowData={rowData}
              tableRef={tableRef}
              withSearchBar
            />
          </Section>
          {appSummary?.app_id ? (
            <ChecklistModal
              appId={appSummary.app_id}
              checklist={checklist}
              isOpen={isCreateToDoModalOpen}
              onClose={handleCreateTodoModal(false)}
              onFailure={handleAddToDoFailure}
              onSuccess={handleAddToDoSuccess}
            />
          ) : null}
        </ContentLayout>
      </Layout>
    </>
  )
}

export default Checklists
